// --------------------------------------------------------------------------
// Context and context provider for CPT to exchange data between
// Body and Header.
// --------------------------------------------------------------------------

import React, { createContext, useState, useContext, useEffect,
  useRef } from 'react';
import { GeoJSON as GeoJSONType } from 'geojson';
import axiosAuthInstance from '../context/security';
import { Map } from 'maplibre-gl';
import { SatData, SpectraData } from '../types/cpt_types';

// Define the shape of your context state and functions
interface CPTContextProps {
  geojsonDataInput: GeoJSONType | null;
  setGeojsonDataInput: (geojsonData: GeoJSONType | null) => void;
  handleGeoJSONChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  mapRef: React.MutableRefObject<Map | null>;
  startDateInput: string;
  setStartDateInput: (startDate: string) => void;
  handleStartDateChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  endDateInput: string;
  setEndDateInput: (endDate: string) => void;
  handleEndDateChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  resolutionInput: number | null;
  setResolutionInput: (resolution: number | null) => void;
  handleResolutionChange: (resolutionAsString: string, resolutionAsNumber: number) => void;
  srtInput: boolean;
  setSRTInput: React.Dispatch<React.SetStateAction<boolean>>;
  handleSRTChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  cloudInput: boolean;
  setCloudInput: React.Dispatch<React.SetStateAction<boolean>>;
  handleCloudChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  satData: SatData;
  selectedPlatforms: { [key: string]: boolean };
  checkAllPlatforms: boolean;
  searchPlatforms: string;
  filteredPlatforms: string[];
  setSelectedPlatforms: React.Dispatch<React.SetStateAction<{ [key: string]: boolean }>>;
  setCheckAllPlatforms: React.Dispatch<React.SetStateAction<boolean>>;
  setSearchPlatforms: React.Dispatch<React.SetStateAction<string>>;
  setFilteredPlatforms: React.Dispatch<React.SetStateAction<string[]>>;
  handleSelectAllPlatformsChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handlePlatformChange: (platformKey: string, isChecked: boolean) => void;
  handlePlatformSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleSelectFilteredPlatforms: (event: React.ChangeEvent<HTMLInputElement>) => void;
  spectraData: SpectraData;
  selectedSpectra: { [key: string]: boolean };
  checkAllSpectra: boolean;
  setSelectedSpectra: React.Dispatch<React.SetStateAction<{ [key: string]: boolean }>>;
  setCheckAllSpectra: React.Dispatch<React.SetStateAction<boolean>>;
  handleSelectAllSpectraChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleSpectraChange: (spectraValue: string, isChecked: boolean) => void;
  selectedSensorClasses: { [key: string]: boolean };
  setSelectedSensorClasses: React.Dispatch<React.SetStateAction<{ [key: string]: boolean }>>;
  handleSensorClassChange: (spectraValue: string, isChecked: boolean) => void;
  dropdownVisible: boolean;
  setDropdownVisible: React.Dispatch<React.SetStateAction<boolean>>;
  searchRef: React.RefObject<HTMLDivElement>;
  errorDB: string;
  errorInputGeoJSON: string;
  setErrorInputGeoJSON: React.Dispatch<React.SetStateAction<string>>;
}

// Create the context with a default value
export const CPTContext = createContext<CPTContextProps | undefined>(undefined);

export function useCPTContext() {
  const context = useContext(CPTContext);
  if (context === undefined) {
    throw new Error('useCPTContext must be used within a CPTProvider');
  }
  return context;
}

// Create a provider component
export const CPTProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [geojsonDataInput, setGeojsonDataInput] = useState<GeoJSONType | null>(null);
  const mapRef = useRef<Map | null>(null);
  const [startDateInput, setStartDateInput] = useState<string>('');
  const [endDateInput, setEndDateInput] = useState<string>('');
  const [resolutionInput, setResolutionInput] = useState<number | null>(null);
  const [srtInput, setSRTInput] = useState(false);
  const [cloudInput, setCloudInput] = useState(false);
  const [selectedPlatforms, setSelectedPlatforms] = useState<{ [key: string]: boolean }>({});
  const [checkAllPlatforms, setCheckAllPlatforms] = useState(false);
  const [searchPlatforms, setSearchPlatforms] = useState("");
  const [filteredPlatforms, setFilteredPlatforms] = useState<string[]>([]);
  const [satData, setSatData] = useState<SatData>({ eo_platforms: {} });
  const [spectraData, setSpectraData] = useState<SpectraData>({ spectra: {} });
  const [selectedSpectra, setSelectedSpectra] = useState<{ [key: string]: boolean }>({});
  const [checkAllSpectra, setCheckAllSpectra] = useState(false);
  const [selectedSensorClasses, setSelectedSensorClasses] = useState<{ [key: string]: boolean }>({});
  const [errorDB, setErrorDB] = useState('');
  const [errorInputGeoJSON, setErrorInputGeoJSON] = useState('');
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const searchRef = useRef<HTMLDivElement | null>(null);

  // Handle data acquisition from database (via backend) for satellite
  // and spectra information.
  useEffect(() => {
    const fetchData = async () => {
      setErrorDB('');
      try {
        const [responseSat, responseSpectra] = await Promise.all([
          axiosAuthInstance.get('/db/sat'),
          axiosAuthInstance.get('/db/spectral-ranges')
        ]);
        setSatData(responseSat.data);
        setSpectraData(responseSpectra.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        setErrorDB("Could not fetch data from database. Please log out and in again.")
      }
    };
    fetchData();
  }, []);

  // Handle platform selection / input + filter and change
  const handleSelectAllPlatformsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setCheckAllPlatforms(isChecked);
    const newSelectedSats = Object.keys(satData.eo_platforms).reduce((acc, platform) => {
      acc[platform] = isChecked;
      return acc;
    }, {} as { [key: string]: boolean });
    setSelectedPlatforms(newSelectedSats);
  };

  const handlePlatformChange = (platformKey: string, isChecked: boolean) => {
    setSelectedPlatforms((prev) => ({
      ...prev,
      [platformKey]: isChecked,
    }));
    if (!isChecked) {
      setCheckAllPlatforms(false);
    }
  };

  const allPlatformsSelected = Object.keys(satData.eo_platforms).every(
    (key) => selectedPlatforms[key]
  );

  useEffect(() => {
    setCheckAllPlatforms(allPlatformsSelected);
  }, [allPlatformsSelected]);

  const handlePlatformSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchPlatforms(event.target.value)
    setDropdownVisible(true);
  };

  const clearSearch = () => {
    setSearchPlatforms('');
    setDropdownVisible(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        clearSearch();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const filtered = searchPlatforms
      ? Object.entries(satData.eo_platforms)
          .filter(([_, platformItem]) => platformItem.platform_name.toLowerCase().includes(searchPlatforms.toLowerCase()))
          .map(([key, _]) => key)
      : [];

    setFilteredPlatforms(filtered);
  }, [searchPlatforms, satData]);

  const handleSelectFilteredPlatforms = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setSelectedPlatforms((prevSelected) => {
      const newSelected = { ...prevSelected };
      filteredPlatforms.forEach((platformKey) => {
        newSelected[platformKey] = isChecked;
      });
      return newSelected;
    });
    if (!isChecked) {
      setCheckAllPlatforms(false);
    }
  };

  // Handle spectra selection / input and change
  const handleSelectAllSpectraChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setCheckAllSpectra(isChecked);
    const newSelectedSpectra = Object.keys(spectraData.spectra).reduce((acc, spectrum) => {
      acc[spectrum] = isChecked;
      return acc;
    }, {} as { [key: string]: boolean });
    setSelectedSpectra(newSelectedSpectra);
  };

  const handleSpectraChange = (spectraValue: string, isChecked: boolean) => {
    setSelectedSpectra((prev) => ({
      ...prev,
      [spectraValue]: isChecked,
    }));
    if (!isChecked) {
      setCheckAllSpectra(false);
    }
  };

  const allSpectraSelected = Object.keys(spectraData.spectra).every(
    (spectra) => selectedSpectra[spectra]
  );

  useEffect(() => {
    setCheckAllSpectra(allSpectraSelected);
  }, [allSpectraSelected]);

  // Handle sensor class selection and change
  const handleSensorClassChange = (sensorClassKey: string, isChecked: boolean) => {
    setSelectedSensorClasses((prev) => ({
      ...prev,
      [sensorClassKey]: isChecked,
    }));
  };

  // Handle GeoJSON input and change
  const handleGeoJSONChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value;
    if (value === '') {
      setGeojsonDataInput(null);
      setErrorInputGeoJSON("");
    } else {
      try {
        const geojson = JSON.parse(value);
        setGeojsonDataInput(geojson);
        setErrorInputGeoJSON("");
      } catch (errorGeoJSON) {
        setErrorInputGeoJSON('Invalid GeoJSON Feature.');
      } 
    }
  };

  // Handle start date input and change
  const handleStartDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setStartDateInput(event.target.value);
  };
  
  // Handle end date input and change
  const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEndDateInput(event.target.value);
  };

  // Handle resolution input and change
  const handleResolutionChange = (resolutionAsString: string, resolutionAsNumber: number) => {
    setResolutionInput(Number.isNaN(resolutionAsNumber) ? null : resolutionAsNumber);
  };

  // Handle SRT switch
  const handleSRTChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSRTInput(e.target.checked);
  };
  
  // Handle Cloud switch
  const handleCloudChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCloudInput(e.target.checked);
  };

  return (
    <CPTContext.Provider value={{
      geojsonDataInput,
      setGeojsonDataInput,
      handleGeoJSONChange,
      mapRef,
      startDateInput,
      setStartDateInput,
      handleStartDateChange,
      endDateInput,
      setEndDateInput,
      handleEndDateChange,
      resolutionInput,
      setResolutionInput,
      handleResolutionChange,
      cloudInput,
      setCloudInput,
      handleCloudChange,
      srtInput,
      setSRTInput,
      handleSRTChange,
      satData,
      selectedPlatforms,
      setSelectedPlatforms,
      checkAllPlatforms,
      setCheckAllPlatforms,
      searchPlatforms,
      setSearchPlatforms,
      filteredPlatforms,
      setFilteredPlatforms,
      handleSelectAllPlatformsChange,
      handlePlatformChange,
      handlePlatformSearchChange,
      handleSelectFilteredPlatforms,
      spectraData,
      selectedSpectra,
      setSelectedSpectra,
      checkAllSpectra,
      setCheckAllSpectra,
      handleSelectAllSpectraChange,
      handleSpectraChange,
      selectedSensorClasses,
      setSelectedSensorClasses,
      handleSensorClassChange,
      dropdownVisible,
      setDropdownVisible,
      searchRef,
      errorDB,
      errorInputGeoJSON,
      setErrorInputGeoJSON,
    }}>
      {children}
    </CPTContext.Provider>
  );
};